import { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, ExternalLink, FileSpreadsheet, Loader2, RefreshCw, TrendingDown, TrendingUp, Users } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Brush } from "recharts";
import { toast } from "sonner";
import { api, API_BASE, tokenStore, formatApiError } from "@/lib/api";
import { saveOrDownload } from "@/lib/downloadFolder";
import HelpButton from "@/components/HelpButton";

export const TRIAL_STATS_ID = "statistieken";
const WINDOW = 26;
const SERIES = [
  { key: "starts", name: "Proefstarts (week)", type: "bar", color: "#0047FF", axis: "l" },
  { key: "conversions", name: "Omzettingen → betaald (week)", type: "bar", color: "#00D05A", axis: "l" },
  { key: "active", name: "Accounts in proefperiode", type: "line", color: "#FF5900", axis: "l" },
  { key: "cumulative", name: "Totaal proefstarts (doorlopend)", type: "line", color: "#000000", axis: "r" },
];

export default function TrialStatsBlock() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState({});
  const [range, setRange] = useState(null);
  const [exporting, setExporting] = useState(false);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const resp = await fetch(`${API_BASE}/admin/stats/trials/export.xlsx`, { headers: { Authorization: `Bearer ${tokenStore.get() || ""}` } });
      if (!resp.ok) throw new Error(await resp.text());
      const name = `proefstarts-per-week-${new Date().toISOString().slice(0, 10)}.xlsx`;
      await saveOrDownload(await resp.blob(), name, "beheer");
      toast.success("Excel gereed.");
    } catch (e) { toast.error("Export mislukt. " + (e.message || "")); }
    setExporting(false);
  };

  const load = useCallback(async () => {
    setBusy(true);
    try { setData((await api.get("/admin/stats/trials")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const weeks = data?.weeks || [];
  const startIndex = Math.max(0, weeks.length - WINDOW);
  const visible = useMemo(() => {
    if (!weeks.length) return { starts: 0, conversions: 0, from: "", to: "" };
    const a = range?.startIndex ?? startIndex, b = range?.endIndex ?? weeks.length - 1;
    const slice = weeks.slice(a, b + 1);
    return { starts: slice.reduce((s, w) => s + w.starts, 0), conversions: slice.reduce((s, w) => s + w.conversions, 0), from: slice[0]?.label, to: slice[slice.length - 1]?.label, n: slice.length };
  }, [weeks, range, startIndex]);

  const t = data?.totals;
  const trend = t ? t.last_4_weeks - t.prev_4_weeks : 0;
  const toggle = (e) => setHidden((h) => ({ ...h, [e.dataKey]: !h[e.dataKey] }));

  return (
    <section id={TRIAL_STATS_ID} className="bg-card border-2 border-foreground heavy-shadow scroll-mt-24" data-testid="trial-stats-block">
      <div className="p-5 border-b-2 border-foreground flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight inline-flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Statistieken · proefstarts per week <HelpButton section="beheer-statistieken" label="Uitleg statistieken" size="sm" /></h2>
          <p className="text-xs text-muted-foreground mt-1">Sleep of verbreed de tijdbalk onder de grafiek om door de weken te schuiven. Klik op een legenda-item om een reeks te verbergen.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="https://meerjarenplanonline.nl/statistieken" target="_blank" rel="noopener noreferrer" data-testid="trial-stats-landing-link"
             className="text-xs font-display uppercase font-bold inline-flex items-center gap-1 hover:underline">Bezoekers landingssite <ExternalLink className="w-3 h-3" /></a>
          <button type="button" onClick={exportXlsx} disabled={exporting || !data} className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="trial-stats-export">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} Excel
          </button>
          <button type="button" onClick={load} disabled={busy} className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="trial-stats-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Ververs
          </button>
        </div>
      </div>

      {!data ? <div className="p-5 text-sm text-muted-foreground">Laden…</div> : (
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="trial-stats-kpis">
            <Kpi label="Proefstarts totaal" value={t.starts} sub="sinds de eerste registratie" testid="ts-kpi-total" />
            <Kpi label="In proefperiode nu" value={t.active_now} sub="actieve proefaccounts" icon={<Users className="w-3.5 h-3.5" />} testid="ts-kpi-active" />
            <Kpi label="Omzettingen" value={t.conversions} sub={`${t.conversion_pct}% van de proefstarts`} testid="ts-kpi-conv" />
            <Kpi label="Laatste 4 weken" value={t.last_4_weeks} sub={`vorige 4 weken: ${t.prev_4_weeks}`} testid="ts-kpi-last4"
                 icon={trend > 0 ? <TrendingUp className="w-3.5 h-3.5 text-positive" /> : trend < 0 ? <TrendingDown className="w-3.5 h-3.5 text-destructive" /> : null} />
            <Kpi label="In beeld" value={visible.starts} sub={`proefstarts · ${visible.from} – ${visible.to}`} testid="ts-kpi-visible" />
            <Kpi label="In beeld omgezet" value={visible.conversions} sub={`${visible.n} weken geselecteerd`} testid="ts-kpi-visible-conv" />
          </div>

          <div className="border-2 border-foreground p-3 bg-card" data-testid="trial-stats-chart">
            <div style={{ width: "100%", height: 360 }}>
              <ResponsiveContainer>
                <ComposedChart data={weeks} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "monospace" }} minTickGap={24} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10, fontFamily: "monospace" }} width={36} allowDecimals={false} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fontFamily: "monospace" }} width={40} allowDecimals={false} />
                  <Tooltip labelFormatter={(l, p) => `${l} · week van ${p?.[0]?.payload?.week_start ? new Date(p[0].payload.week_start).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" }) : ""}`} />
                  <Legend verticalAlign="top" wrapperStyle={{ fontSize: 11, cursor: "pointer", paddingBottom: 8 }} onClick={toggle} formatter={(v, e) => <span style={{ textDecoration: hidden[e.dataKey] ? "line-through" : "none", opacity: hidden[e.dataKey] ? 0.5 : 1 }}>{v}</span>} />
                  {SERIES.map((s) => s.type === "bar"
                    ? <Bar key={s.key} yAxisId={s.axis} dataKey={s.key} name={s.name} fill={s.color} hide={!!hidden[s.key]} />
                    : <Line key={s.key} yAxisId={s.axis} dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2} dot={false} hide={!!hidden[s.key]} type={s.key === "cumulative" ? "stepAfter" : "linear"} />)}
                  <Brush dataKey="label" height={28} stroke="#000" fill="#fff" travellerWidth={10} startIndex={startIndex} endIndex={Math.max(0, weeks.length - 1)}
                         onChange={(r) => setRange(r)} tickFormatter={(v) => v} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Linkeras: aantallen per week en accounts in proefperiode (stand aan het einde van de week). Rechteras: doorlopend totaal proefstarts. Standaardvenster: laatste {WINDOW} weken.</p>
          </div>
        </div>
      )}
    </section>
  );
}

function Kpi({ label, value, sub, icon, testid }) {
  return (
    <div className="border-2 border-foreground p-3 bg-card" data-testid={testid}>
      <div className="stat-label flex items-center gap-1">{icon}{label}</div>
      <div className="font-display font-black text-xl mt-1 font-mono-num">{Number(value).toLocaleString("nl-NL")}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
